import { useCallback, useEffect, useState } from 'react';
import { useQuill } from 'react-quilljs';
import 'quill/dist/quill.snow.css';
import Quill, { Delta } from 'quill';
import { MentionBlot, Mention } from 'quill-mention';
import 'quill-mention/dist/quill.mention.css';
import { isInlineImage } from '../../../utils/htmlSanitizer';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { useLatest } from '../../../utils/useLatest';
import { Tooltip } from '@mui/material';
import { MentionableUser } from '../../../types';

Quill.register('blots/mention', MentionBlot);
Quill.register('modules/mention', Mention);

/** The formats the editor keeps when it reads text in, by paste or as a draft. A video is not one: no card shows it. */
export const EDITOR_FORMATS = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'indent',
  'link',
  'image',
  'mention',
  'code-block'
];

/** A paste rule: every card keeps an image only inline, so one pasted from a web page would show here alone. */
const inlineImagesOnly = (node: Node, delta: Delta): Delta =>
  isInlineImage((node as Element).getAttribute('src') ?? '') ? delta : new Delta();

export type AtMentionData = {
  id: string;
  value: string;
};
export interface TextEditorProps {
  commentText: string;
  handleCancel: () => void;
  handleSend: (commentText: string) => void;
  placeholder: string;
  portalUsers?: MentionableUser[];
  readonly?: boolean;
  sendDisabled?: boolean;
}

export function TextEditor({
  commentText,
  handleCancel,
  handleSend,
  placeholder,
  portalUsers = [],
  readonly = false,
  sendDisabled = false
}: TextEditorProps) {
  const [comment, setComment] = useState<string>(commentText || '');

  const sourceFunction = (searchTerm: string, renderList: (vals: AtMentionData[], search: string) => void) => {
    const values = portalUsers.map(
      // upstackers have no email, so fall back to id — the API resolves numeric ids to a user
      (user) => ({ id: user.email || user.id, value: `${user.first_name} ${user.last_name}` }) as AtMentionData
    );

    if (searchTerm.length === 0) {
      renderList(values, searchTerm);
    } else {
      const matches = values.filter((val) => val.value.toLowerCase().includes(searchTerm.toLowerCase()));
      renderList(matches, searchTerm);
    }
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic'],
      ['link'],
      [{ list: 'bullet' }, { list: 'ordered' }],
      ['code-block']
    ],
    clipboard: { matchers: [['IMG', inlineImagesOnly]] },
    mention: {
      allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
      mentionDenotationChars: ['@'],
      source: useCallback(sourceFunction, [portalUsers]),
      mentionContainerClass:
        'border-2 rounded bg-grey-0 min-w-[25%] max-w-[50%] max-h-[25vh] bg-white z-40 overflow-auto',
      listItemClass: 'px-2 py-2 h-full w-full text-sm text-indigo cursor-pointer text-ellipsis'
    }
  };

  const { quill, quillRef } = useQuill({
    theme: 'snow',
    modules,
    formats: EDITOR_FORMATS,
    placeholder,
    readOnly: readonly
  });

  // Ctrl+Enter is bound in the effect that loads the text, so it reads both through refs rather than re-running it.
  const handleSendRef = useLatest(handleSend);
  const sendDisabledRef = useLatest(sendDisabled);

  useEffect(() => {
    if (quill) {
      quill.root.innerHTML = commentText || '';
      const handleTextChange = () => {
        const currentHtml = quill.root.innerHTML;
        const isContentEmpty = currentHtml === '<p><br></p>' || currentHtml.trim() === '';
        setComment(isContentEmpty ? '' : currentHtml);
      };
      quill.on('text-change', handleTextChange);

      const handleKeyDown = (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
          event.preventDefault();
          if (!sendDisabledRef.current && !isSendButtonDisabled()) handleSendRef.current(quill.root.innerHTML);
        }
      };

      quill.root.addEventListener('keydown', handleKeyDown);

      return () => {
        // Only this listener: the mention module listens for the same event on the same editor.
        quill.off('text-change', handleTextChange);
        quill.root.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [quill, commentText]);

  const isSendButtonDisabled = () => {
    return !quill || quill.getText().trim().length === 0;
  };

  return (
    <div data-testid="quillcontainer">
      <div ref={quillRef} />
      <div className="flex mt-auto justify-end gap-5 pt-4 pb-4 self-end w-full">
        <button
          className={DEFAULT_CANCEL_BUTTON_STYLE}
          onClick={() => handleCancel()}>
          Cancel
        </button>
        <Tooltip title="Ctrl + Enter">
          <button
            className={DEFAULT_ADVANCE_BUTTON_STYLE}
            onClick={() => handleSend(comment)}
            disabled={sendDisabled || isSendButtonDisabled()}>
            Send
          </button>
        </Tooltip>
      </div>
    </div>
  );
}
